import React, { Fragment } from 'react';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import ColorPicker from '../../../Components/ColorPicker';
import LabelColorSlider from '../../../Components/LabelColorSlider';

const toggleStyle = { color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' };

const ToggleSetting = ({ label, value, onEnable, onDisable }) => (
  <div className="form-item">
    <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
      {label}
    </label>
    {value ? (
      <ToggleOn style={toggleStyle} onClick={onDisable} />
    ) : (
      <ToggleOff style={toggleStyle} onClick={onEnable} />
    )}
  </div>
);

const LabelWidgetSettings = ({
  edit,
  setEdit,
  t,
  selectedDatastream,
  getDsMinMax,
  themeColorKey,
  themeColorValue,
}) => {
  if (edit.type !== 'label') {
    return null;
  }

  return (
    <Fragment>
      <ToggleSetting
        label={t.settingsLabels?.showValueLine || "Show Value Line"}
        value={edit.props.showValueLine !== false}
        onDisable={() => setEdit(prev => ({
          ...prev,
          props: { ...prev.props, showValueLine: false }
        }))}
        onEnable={() => setEdit(prev => ({
          ...prev,
          props: { ...prev.props, showValueLine: true }
        }))}
      />
      <ToggleSetting
        label={t.settingsLabels?.labelShadowEnabled || "Enable Shadow Glow"}
        value={Boolean(edit.props.labelShadowEnabled)}
        onDisable={() => setEdit(prev => ({
          ...prev,
          props: { ...prev.props, labelShadowEnabled: false }
        }))}
        onEnable={() => setEdit(prev => ({
          ...prev,
          props: { ...prev.props, labelShadowEnabled: true }
        }))}
      />

      {edit.props.labelShadowEnabled ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {t.settingsLabels?.labelShadowBlur || "Shadow Blur (px)"}
          </label>
          <input
            type='number'
            min='0'
            max='120'
            step='1'
            className='add-column'
            style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
            value={edit.props.labelShadowBlur ?? 28}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = Number(raw);
              setEdit(prev => ({
                ...prev,
                props: {
                  ...prev.props,
                  labelShadowBlur: Number.isFinite(parsed) ? Math.max(0, parsed) : 0
                }
              }));
            }}
          />
        </div>
      ) : null}

      {edit.props.labelShadowEnabled ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {t.settingsLabels?.labelShadowStrength || "Shadow Strength"}
          </label>
          <input
            type='number'
            min='0.1'
            step='0.1'
            className='add-column'
            style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
            value={edit.props.labelShadowStrength ?? 2}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = Number(raw);
              setEdit(prev => ({
                ...prev,
                props: {
                  ...prev.props,
                  labelShadowStrength: Number.isFinite(parsed) ? Math.max(0.1, parsed) : 0.1
                }
              }));
            }}
          />
        </div>
      ) : null}

      {edit.props.labelShadowEnabled ? (
        <ToggleSetting
          label={t.settingsLabels?.labelShadowUseValueColor || "Shadow follows text/value color"}
          value={Boolean(edit.props.labelShadowUseValueColor)}
          onDisable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowUseValueColor: false }
          }))}
          onEnable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowUseValueColor: true }
          }))}
        />
      ) : null}

      {edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor ? (
        <ToggleSetting
          label={t.settingsLabels?.labelShadowChangeColorBasedOnValue || "Change Shadow Color Based On Value"}
          value={Boolean(edit.props.labelShadowChangeColorBasedOnValue)}
          onDisable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowChangeColorBasedOnValue: false }
          }))}
          onEnable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowChangeColorBasedOnValue: true }
          }))}
        />
      ) : null}

      {edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && edit.props.labelShadowChangeColorBasedOnValue ? (
        <ToggleSetting
          label={t.settingsLabels?.labelShadowColorGrading || "Shadow Color Grading"}
          value={Boolean(edit.props.labelShadowColorGrading)}
          onDisable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowColorGrading: false }
          }))}
          onEnable={() => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, labelShadowColorGrading: true }
          }))}
        />
      ) : null}

      {edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && edit.props.labelShadowChangeColorBasedOnValue ? (
        <LabelColorSlider
          minValue={getDsMinMax(selectedDatastream).min ?? edit.props.min}
          maxValue={getDsMinMax(selectedDatastream).max ?? edit.props.max}
          stops={themeColorValue(edit.props, 'labelShadowColors', [])}
          colorGrading={Boolean(edit.props.labelShadowColorGrading)}
          setStops={(updated) => setEdit(prev => ({
            ...prev,
            props: { ...prev.props, [themeColorKey('labelShadowColors')]: updated }
          }))}
        />
      ) : null}

      {edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && !edit.props.labelShadowChangeColorBasedOnValue ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {t.settingsLabels?.labelShadowColor || "Shadow Color"}
          </label>
          <ColorPicker
            id="label-shadow-color"
            color={themeColorValue(
              edit.props,
              'labelShadowColor',
              themeColorValue(edit.props, 'color', '#00f0f0')
            )}
            setColor={(c) => setEdit(prev => ({
              ...prev,
              props: { ...prev.props, [themeColorKey('labelShadowColor')]: c }
            }))}
          />
        </div>
      ) : null}
    </Fragment>
  );
};

export default LabelWidgetSettings;
